import {createSelector} from '@ngrx/store';
import {AppState} from '../states/app.state';
import {identity} from 'rxjs';
import {BelegLabels, getBelegLabels} from '../../i18n/beleg-labels';
import {Umsatzart} from '../../util/umsatzart.enum';
import {isLieferschein} from '../../util/typeguards';


export class FakturierungsbelegFormSelectors {
  /**
   * Die `select`-Eigenschaft ist ein Objekt, das Funktionen definiert, jede Funktion selektiert eine
   * bestimmte Eigenschaft aus dem State.
   * Das sind grundlegende Selektoren, die im ganzen Projekt als Bausteine für komplexere Selektoren wiederverwendet
   * werden können.
   */
  private static select = {
    fakturierungsbelegDto: (state: AppState) => state.fakturierungsbelegForm.fakturierungsbeleg,
    positionen: (state: AppState) => state.fakturierungsbelegForm.positionen,
    logoUrl: (state: AppState) => state.fakturierungsbelegForm.logoUrl,
    kontaktMode: (state: AppState) => state.fakturierungsbelegForm.kontaktMode,
    vorlaufzeileMode: (state: AppState) => state.fakturierungsbelegForm.vorlaufzeileMode,
    vorlaufzeile: (state: AppState) => state.fakturierungsbelegForm.fakturierungsbeleg.vorlaufzeile,
    positionMengeModes: (state: AppState) => state.fakturierungsbelegForm.positionMengeModes,
    positionEinzelpreisNettoModes: (state: AppState) => state.fakturierungsbelegForm.positionEinzelpreisNettoModes,
    positionBeschreibungModes: (state: AppState) => state.fakturierungsbelegForm.positionBeschreibungModes,
    addPositionMode: (state: AppState) => state.fakturierungsbelegForm.addPositionMode,
    belegdatum: (state: AppState) => state.fakturierungsbelegForm.fakturierungsbeleg?.datum,
    leistungsdatum: (state: AppState) => state.fakturierungsbelegForm.fakturierungsbeleg?.leistungsdatum,
    zahlungszielTage: (state: AppState) => state.fakturierungsbelegForm.fakturierungsbeleg?.zahlungszielTage,
    kundeId: (state: AppState) => state.fakturierungsbelegForm.fakturierungsbeleg?.kundeId,
    kunde: (state: AppState) => state.fakturierungsbelegForm.fakturierungsbeleg?.kunde,
    belegdatumMode: (state: AppState) => state.fakturierungsbelegForm.belegdatumMode,
    leistungsLieferdatumControlMode: (state: AppState) => state.fakturierungsbelegForm.leistungsLieferdatumControlMode,
    leistungsLieferdatumDateMode: (state: AppState) => state.fakturierungsbelegForm.leistungsLieferdatumDateMode,
    absenderZeile: (state: AppState) => state.fakturierungsbelegForm.fakturierungsbeleg.betrieb?.absenderZeile,
    fakturierungsbeleg: (state: AppState) => state.fakturierungsbelegForm.fakturierungsbeleg,
    zahlungskonditionen: (state: AppState) => state.fakturierungsbelegForm.fakturierungsbeleg?.zahlungskonditionen,
    belegart: (state: AppState) => state.fakturierungsbelegForm.fakturierungsbeleg.belegart,
    umsatzart: (state: AppState) => state.fakturierungsbelegForm.fakturierungsbeleg?.umsatzart,
    sprache: (state: AppState) => state.fakturierungsbelegForm.fakturierungsbeleg?.sprache,
    referenzfelderSichtbar: (state: AppState) => state.fakturierungsbelegForm.referenzfelderSichtbar,
    referenzfelderHydratedBelegId: (state: AppState) => state.fakturierungsbelegForm.referenzfelderHydratedBelegId,
  };

  /**
   * Selektiert die fakturierungsbelegDto.
   */
  public static fakturierungsbelegDto = createSelector(
    FakturierungsbelegFormSelectors.select.fakturierungsbelegDto,
    identity,
  );

  /**
   * Selektiert die belegId.
   */
  public static belegId = createSelector(
    FakturierungsbelegFormSelectors.select.fakturierungsbelegDto,
    fakturierungsbelegDto => fakturierungsbelegDto?.id
  );

  /**
   * Selektiert die Kunden-ID des Fakturierungsbelegs.
   */
  public static kundeId = createSelector(
    FakturierungsbelegFormSelectors.select.kundeId,
    identity,
  );

  /**
   * Selektiert den Kunden des Fakturierungsbelegs.
   */
  public static kunde = createSelector(
    FakturierungsbelegFormSelectors.select.kunde,
    identity,
  );

  /**
   * Selektiert die Positionen des Fakturierungsbelegs.
   */
  public static positionen = createSelector(
    FakturierungsbelegFormSelectors.select.positionen,
    identity,
  );

  /**
   * Selektiert den KontaktMode.
   */
  public static KontaktMode = createSelector(
    FakturierungsbelegFormSelectors.select.kontaktMode,
    identity,
  );

  /**
   * Selektiert den VorlaufzeileMode.
   */
  public static vorlaufzeileMode = createSelector(
    FakturierungsbelegFormSelectors.select.vorlaufzeileMode,
    identity,
  );

  /**
   * Selektiert die Vorlaufzeile des Fakturierungsbelegs.
   */
  public static vorlaufzeile = createSelector(
    FakturierungsbelegFormSelectors.select.vorlaufzeile,
    identity,
  );

  /**
   * Selektiert das Logo des Inhabers.
   */
  public static logoUrl = createSelector(
    this.select.logoUrl,
    identity,
  );

  /**
   * Selektiert die Position-Menge-Modi der Positionen.
   */
  public static positionMengeModes = createSelector(
    FakturierungsbelegFormSelectors.select.positionMengeModes,
    identity,
  );

  /**
   * Selektiert die Betragssumme-Modi der Positionen.
   */
  public static positionEinzelpreisNettoModes = createSelector(
    FakturierungsbelegFormSelectors.select.positionEinzelpreisNettoModes,
    identity,
  );

  /**
   * Selektiert die Beschreibungs-Modi der Positionen.
   */
  public static positionBeschreibungModes = createSelector(
    FakturierungsbelegFormSelectors.select.positionBeschreibungModes,
    identity,
  );

  /**
   * Liefert true, wenn mindestens eine Positions-Beschreibung im Edit-Mode ist.
   */
  public static beschreibungEditModeActive = createSelector(
    FakturierungsbelegFormSelectors.select.positionBeschreibungModes,
    modes => modes.some(item => item.mode === 'edit'),
  );

  /**
   * Selektiert den Modus der Produkt-Hinzufügen-Funktion.
   */
  public static addPositionMode = createSelector(
    FakturierungsbelegFormSelectors.select.addPositionMode,
    identity,
  );

  /**
   * Selektiert das Belegdatum.
   */
  public static belegdatum = createSelector(
    FakturierungsbelegFormSelectors.select.belegdatum,
    identity,
  );

  /**
   * Selektieren der Belegart
   */
  public static belegart = createSelector(
    FakturierungsbelegFormSelectors.select.belegart,
    identity,
  );

  /**
   * Selektiert das Leistungsdatum.
   */
  public static leistungsdatum = createSelector(
    FakturierungsbelegFormSelectors.select.leistungsdatum,
    identity,
  );

  /**
   * Selektiert die Zahlungsziel-Tage.
   */
  public static zahlungszielTage = createSelector(
    FakturierungsbelegFormSelectors.select.zahlungszielTage,
    identity,
  );

  /**
   * Selektiert die Sichtbarkeit der Referenzfelder.
   */
  public static referenzfelderSichtbar = createSelector(
    FakturierungsbelegFormSelectors.select.referenzfelderSichtbar,
    identity,
  );

  /**
   * Selektiert den Beleg, dessen Referenzfeld-Sichtbarkeit bereits abgeleitet wurde.
   */
  public static referenzfelderHydratedBelegId = createSelector(
    FakturierungsbelegFormSelectors.select.referenzfelderHydratedBelegId,
    identity,
  );

  /**
   * Selektiert die Werte der Referenzfelder.
   */
  public static referenzfelder = createSelector(
    FakturierungsbelegFormSelectors.select.fakturierungsbeleg,
    beleg => ({
      bestellnummer: beleg?.bestellnummer,
      lieferantennummer: beleg?.lieferantennummer,
      vertragsnummer: beleg?.vertragsnummer,
      projektnummer: beleg?.projektnummer,
      leitwegId: beleg?.leitwegId,
    }),
  );

  /**
   * Selektiert den Modus des Belegdatums.
   */
  public static belegdatumMode = createSelector(
    FakturierungsbelegFormSelectors.select.belegdatumMode,
    identity,
  );

  /**
   * Selektiert den Modus des Controls des Leistungs- und Lieferdatum.
   */
  public static leistungsLieferdatumControlMode = createSelector(
    FakturierungsbelegFormSelectors.select.leistungsLieferdatumControlMode,
    identity,
  );

  /**
   * Selektiert den Modus des Leistungs- und Lieferdatum.
   */
  public static leistungsLieferdatumDateMode = createSelector(
    FakturierungsbelegFormSelectors.select.leistungsLieferdatumDateMode,
    identity,
  );

  /**
   * Selektiert den Text der Absenderzeile.
   */
  public static absenderZeile = createSelector(
    FakturierungsbelegFormSelectors.select.absenderZeile,
    identity,
  );

  /**
   * Selektiert den aktuellen Fakturierungsbeleg.
   */
  public static fakturierungsbeleg = createSelector(
    FakturierungsbelegFormSelectors.select.fakturierungsbeleg,
    identity,
  );

  /**
   * Selektiert die Zahlungskonditionen des Fakturierungsbelegs.
   */
  public static zahlungskonditionen = createSelector(
    FakturierungsbelegFormSelectors.select.zahlungskonditionen,
    identity,
  );

  /**
   * Selektiert alle Informationen für die Fusszeile.
   */
  public static fusszeile = createSelector(
    FakturierungsbelegFormSelectors.select.fakturierungsbeleg,
    fakturierungsbeleg => {

      return {
        adresseFusszeile: fakturierungsbeleg?.betrieb?.adresseFusszeile ?? undefined,
        email: fakturierungsbeleg?.einstellung?.kontakt?.emailAdresse ?? undefined,
        telefon: fakturierungsbeleg?.einstellung?.kontakt?.telefonnummer ?? undefined,
        vorname: fakturierungsbeleg?.einstellung?.kontakt?.vorname ?? undefined,
        nachname: fakturierungsbeleg?.einstellung?.kontakt?.nachname ?? undefined,
        internetadresse: fakturierungsbeleg?.einstellung?.kontakt?.internetadresse ?? undefined,
        steuernummer: fakturierungsbeleg.betrieb?.steuernummer ?? undefined,
        ustIdNummer: fakturierungsbeleg.betrieb?.ustIdNummer ?? undefined,
        vvvoNummer: fakturierungsbeleg.einstellung?.vvvoNummer ?? undefined,
        oekoKontrollnummer: fakturierungsbeleg.einstellung?.oekoKontrollnummer ?? undefined,
        glaeubigerId: fakturierungsbeleg.betrieb?.glaeubigerId ?? undefined,
        kontoinhaber: fakturierungsbeleg.einstellung?.bankkonto?.kontoinhaber ?? undefined,
        iban: fakturierungsbeleg.einstellung?.bankkonto?.iban ?? undefined,
        bic: fakturierungsbeleg.einstellung?.bankkonto?.bic ?? undefined,
        bankbezeichnung: fakturierungsbeleg.einstellung?.bankkonto?.bankbezeichnung ?? undefined,
      };
    }
  );

  /**
   * Selektiert die vom Service ermittelte Umsatzart des Belegs.
   */
  public static umsatzart = createSelector(
    FakturierungsbelegFormSelectors.select.umsatzart,
    (umsatzart): string | undefined => umsatzart,
  );

  /**
   * Liefert true, wenn der Beleg als steuerfreie innergemeinschaftliche Lieferung behandelt wird.
   */
  public static isIgl = createSelector(
    FakturierungsbelegFormSelectors.umsatzart,
    umsatzart => umsatzart === Umsatzart.InnergemeinschaftlicheLieferung,
  );

  /**
   * Liefert true, wenn die USt-Spalte (und ihre Summen) im Belegtisch angezeigt werden soll.
   * Bei INNERGEMEINSCHAFTLICHE_LIEFERUNG entfaellt der Steuerausweis - die Spalte wird ausgeblendet.
   */
  public static showUstColumn = createSelector(
    FakturierungsbelegFormSelectors.isIgl,
    isIgl => !isIgl,
  );

  /**
   * Selektiert die vom Backend ermittelte Sprache des Beleg-Layouts ('DE' | 'EN').
   * Faellt auf 'DE' zurueck, wenn das Feld am Beleg noch nicht gesetzt ist
   * (z.B. Bestandsbelege vor Einfuehrung des Feldes).
   */
  public static sprache = createSelector(
    FakturierungsbelegFormSelectors.select.sprache,
    (sprache): string => sprache ?? 'DE',
  );

  /**
   * Liefert die sprachabhaengigen Labels fuer das WYSIWYG-Layout des Belegs.
   * Memoisiert auf Basis der Beleg-Sprache und der Belegart (Lieferschein-Overlay).
   */
  public static belegLabels = createSelector(
    FakturierungsbelegFormSelectors.sprache,
    FakturierungsbelegFormSelectors.belegart,
    (sprache, belegart): BelegLabels => getBelegLabels(sprache, belegart),
  );

  /**
   * Liefert true, wenn Preis- und Zahlungsangaben (Einzel-/Gesamtpreise,
   * Summenblock, Faelligkeit, Zahlungskonditionen) im Beleg angezeigt werden
   * sollen. Der Lieferschein traegt keine Betraege und Zahlungsdaten, deshalb
   * gilt fuer alle drei Bereiche dieselbe Regel — sollte eine kuenftige
   * Belegart sie unterschiedlich gaten, hier wieder auftrennen.
   */
  public static showPreisUndZahlungsangaben = createSelector(
    FakturierungsbelegFormSelectors.belegart,
    belegart => !isLieferschein(belegart),
  );

  /**
   * Liefert das Locale fuer die Zahlen-/Waehrungsformatierung passend zur Beleg-Sprache.
   * Memoisiert auf Basis der Beleg-Sprache.
   */
  public static belegLocale = createSelector(
    FakturierungsbelegFormSelectors.sprache,
    (sprache): string => sprache === 'EN' ? 'en-US' : 'de-DE',
  );
}
